/**
 * OnboardingStep — the bottom white card in the split-screen onboarding
 * layout (016 Onboarding Wizard, spec FR-002, FR-003, FR-011).
 *
 * The card houses:
 *   1. Progress indicator (dot strip)
 *   2. Step content slot (illustration on steps 0–2, toggles on step 3)
 *   3. Full-width pill-shaped CTA button
 *
 * Title + description live in the upper teal area rendered by the wizard
 * orchestrator — this component only owns the card.
 */

import type { JSX, ReactNode } from 'react';

import { Button } from '../primitives/Button.js';

export interface OnboardingStepProps {
  /** Caller-supplied content area: illustration (steps 0–2) or toggles (step 3). */
  readonly children: ReactNode;
  /** Progress indicator slot — `OnboardingWizard` renders `<OnboardingProgress>` here. */
  readonly progress: ReactNode;
  readonly ctaLabel: string;
  readonly onCta: () => void;
}

// Bottom white card — `rounded-t-3xl` matches the prototype's `rounded-t-[3rem]`
// (our --radius-3xl is 1.5rem which reads the same at mobile widths; the 3rem
// was absolute to the proto's fixed viewport — we scale proportionally).
const cardClasses =
  'relative rounded-t-3xl bg-card shadow-2xl flex flex-col min-h-onboarding-card-min max-h-onboarding-card-max';
const progressWrapperClasses = 'flex justify-center pt-8 pb-6';
const contentClasses =
  'flex-1 flex flex-col items-center justify-center px-6 min-h-0 overflow-y-auto';
const ctaWrapperClasses = 'px-6 pb-8 pt-4';
const ctaButtonClasses = 'w-full min-h-14 rounded-full text-lg';

export function OnboardingStep({
  children,
  progress,
  ctaLabel,
  onCta,
}: OnboardingStepProps): JSX.Element {
  return (
    <div className={cardClasses}>
      <div className={progressWrapperClasses}>{progress}</div>
      <div className={contentClasses}>{children}</div>
      <div className={ctaWrapperClasses}>
        <Button onClick={onCta} className={ctaButtonClasses}>
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
}
